import type {
  ApiErrorBody,
  CatalogModule,
  ClientDetail,
  ClientListItem,
  Dashboard,
  DashboardQuery,
  Deliverable,
  Me,
  ModuleSelection,
  MonthlyEntry,
  TokenResponse,
  Video,
} from './types';

/** Onde guardar a sessão. Web: localStorage. App mobile: SecureStore/Keychain. */
export interface SessionStorage {
  load(): Session | null;
  save(session: Session | null): void;
}

export interface Session {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface ApiClientOptions {
  /** Origem da API, sem barra no fim. Vazio = mesma origem (proxy do Vite em desenvolvimento). */
  baseUrl: string;
  storage: SessionStorage;
  /** Chamado quando o refresh falha: a interface volta para o login. */
  onSessionExpired?: () => void;
  /** Tempo máximo por requisição. A API no plano gratuito pode levar ~1 min para acordar. */
  timeoutMs?: number;
  fetch?: typeof fetch;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const FALLBACK_MESSAGES: Record<number, string> = {
  401: 'E-mail ou senha incorretos.',
  403: 'Você não tem acesso a esta área.',
  404: 'Não encontramos o que você procurou.',
  429: 'Muitas tentativas. Espere um minuto e tente de novo.',
};

export function createApiClient(options: ApiClientOptions) {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const timeoutMs = options.timeoutMs ?? 75_000;
  const root = `${options.baseUrl.replace(/\/$/, '')}/api/v1`;
  let session = options.storage.load();
  let refreshing: Promise<boolean> | null = null;

  function saveTokens(tokens: TokenResponse | null) {
    session = tokens
      ? { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, expiresAt: Date.now() + tokens.expiresIn * 1000 }
      : null;
    options.storage.save(session);
  }

  async function send(method: string, url: string, body: unknown, withAuth: boolean): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (withAuth && session) headers.Authorization = `Bearer ${session.accessToken}`;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await doFetch(url, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      });
    } catch {
      throw new ApiError(0, 'Sem conexão com o servidor. Verifique sua internet e tente de novo.');
    } finally {
      clearTimeout(timer);
    }
  }

  function tryRefresh(): Promise<boolean> {
    if (!session) return Promise.resolve(false);
    refreshing ??= (async () => {
      try {
        const res = await send('POST', `${root}/auth/refresh`, { refreshToken: session!.refreshToken }, false);
        if (!res.ok) return false;
        saveTokens((await res.json()) as TokenResponse);
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
    const res = await send(method, `${root}${path}`, body, true);

    if (res.status === 401 && retry && session) {
      if (await tryRefresh()) return request<T>(method, path, body, false);
      saveTokens(null);
      options.onSessionExpired?.();
      throw new ApiError(401, 'Sua sessão expirou. Entre de novo.');
    }

    if (!res.ok) {
      let payload: ApiErrorBody | null = null;
      try {
        payload = (await res.json()) as ApiErrorBody;
      } catch {
        /* corpo vazio */
      }
      throw new ApiError(
        res.status,
        payload?.message ?? FALLBACK_MESSAGES[res.status] ?? 'Algo deu errado. Tente de novo em instantes.',
        payload?.fields ?? {},
      );
    }

    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** Aceita só o mês ("2026-08", como antes) ou uma consulta com intervalo ({ from, to }). */
  const q = (query?: string | DashboardQuery | null) => {
    const { period, from, to }: DashboardQuery = typeof query === 'string' ? { period: query } : (query ?? {});
    const parts: [string, string][] = [];
    if (from && to) {
      parts.push(['from', from], ['to', to]);
    } else if (period) {
      parts.push(['period', period]);
    }
    return parts.length ? `?${parts.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')}` : '';
  };

  return {
    hasSession: () => session !== null,

    /** Acorda a API (plano gratuito dorme após 15 min sem uso). Não toca no banco. */
    async ping(): Promise<boolean> {
      try {
        const res = await send('GET', `${options.baseUrl.replace(/\/$/, '')}/health/live`, undefined, false);
        return res.ok;
      } catch {
        return false;
      }
    },

    async login(email: string, password: string) {
      const res = await send('POST', `${root}/auth/login`, { email, password }, false);
      if (!res.ok) {
        let payload: ApiErrorBody | null = null;
        try {
          payload = (await res.json()) as ApiErrorBody;
        } catch {
          /* corpo vazio */
        }
        throw new ApiError(
          res.status,
          payload?.message ?? FALLBACK_MESSAGES[res.status] ?? 'Não foi possível entrar.',
          payload?.fields ?? {},
        );
      }
      saveTokens((await res.json()) as TokenResponse);
    },
    logout: () => saveTokens(null),
    me: () => request<Me>('GET', '/auth/me'),

    /** Troca a própria senha. Os outros dispositivos saem; este recebe tokens novos e continua logado. */
    async changePassword(currentPassword: string, newPassword: string) {
      saveTokens(await request<TokenResponse>('POST', '/auth/password', { currentPassword, newPassword }));
    },

    /** Sair de todos os dispositivos, inclusive este. */
    async logoutAll() {
      try {
        await request<void>('POST', '/auth/logout-all');
      } finally {
        saveTokens(null);
      }
    },

    // Cliente
    myDashboard: (query?: string | DashboardQuery | null) => request<Dashboard>('GET', `/me/dashboard${q(query)}`),
    myDeliverables: () => request<Deliverable[]>('GET', '/me/deliverables'),

    // Admin
    catalog: () => request<CatalogModule[]>('GET', '/admin/catalog'),
    clients: () => request<ClientListItem[]>('GET', '/admin/clients'),
    client: (id: string) => request<ClientDetail>('GET', `/admin/clients/${id}`),
    createClient: (body: unknown) => request<ClientDetail>('POST', '/admin/clients', body),
    updateClient: (id: string, body: unknown) => request<ClientDetail>('PUT', `/admin/clients/${id}`, body),
    setModules: (id: string, modules: ModuleSelection[]) => request<ClientDetail>('PUT', `/admin/clients/${id}/modules`, modules),
    resetPassword: (id: string, userId: string, password: string) =>
      request<void>('POST', `/admin/clients/${id}/users/${userId}/password`, { password }),
    clientDashboard: (id: string, query?: string | DashboardQuery | null) =>
      request<Dashboard>('GET', `/admin/clients/${id}/dashboard${q(query)}`),
    monthly: (id: string, module: string, period: string) =>
      request<MonthlyEntry>('GET', `/admin/clients/${id}/metrics/${module}/${period}`),
    saveMonthly: (id: string, module: string, period: string, values: Record<string, number | null>) =>
      request<void>('PUT', `/admin/clients/${id}/metrics/${module}/${period}`, { values }),
    clientDeliverables: (id: string) => request<Deliverable[]>('GET', `/admin/clients/${id}/deliverables`),
    createDeliverable: (id: string, body: unknown) => request<Deliverable>('POST', `/admin/clients/${id}/deliverables`, body),
    updateDeliverable: (deliverableId: string, body: unknown) => request<Deliverable>('PUT', `/admin/deliverables/${deliverableId}`, body),
    addVersion: (deliverableId: string, fileUrl: string, notes: string | null) =>
      request<Deliverable>('POST', `/admin/deliverables/${deliverableId}/versions`, { fileUrl, notes }),
    deleteDeliverable: (deliverableId: string) => request<void>('DELETE', `/admin/deliverables/${deliverableId}`),
    videos: (id: string, period: string) => request<Video[]>('GET', `/admin/clients/${id}/videos?period=${period}`),
    createVideo: (id: string, body: unknown) => request<Video>('POST', `/admin/clients/${id}/videos`, body),
    updateVideo: (videoId: string, body: unknown) => request<Video>('PUT', `/admin/videos/${videoId}`, body),
    deleteVideo: (videoId: string) => request<void>('DELETE', `/admin/videos/${videoId}`),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
